import { Fragment } from 'react'
import { Ban, CheckCircle2, Loader2, XCircle } from 'lucide-react'
import clsx from 'clsx'
import { PATCH_ENVIRONMENTS, type PatchCampaign, type PatchEnvironment } from '@/types/patching'
import { findStage, isRunningStatus, isStageIncluded, stageStatusLabel, stepStateFor, type StepState } from '@/utils/patchStage'
import styles from './Patching.module.css'

interface StageStepperProps {
  campaign: PatchCampaign
  /** `mini` is the compact variant used in list rows. */
  size?: 'full' | 'mini'
  selected?: PatchEnvironment
  onSelect?: (env: PatchEnvironment) => void
}

type DisplayState = StepState | 'absent'

const FULL_CLASS: Record<DisplayState, string> = {
  done: styles.stepDone,
  active: styles.stepActive,
  failed: styles.stepFailed,
  pending: '',
  rejected: styles.stepRejected,
  cancelled: styles.stepCancelled,
  absent: styles.stepAbsent,
}

const MINI_CLASS: Record<DisplayState, string> = {
  done: styles.badgeDone,
  active: styles.badgeActive,
  failed: styles.badgeFailed,
  pending: '',
  rejected: styles.badgeRejected,
  cancelled: styles.badgeCancelled,
  absent: styles.stepAbsent,
}

function StateIcon({ state, running, index, size }: { state: DisplayState; running: boolean; index: number; size: number }) {
  if (running) return <Loader2 size={size} className={styles.spin} aria-hidden="true" />
  if (state === 'done') return <CheckCircle2 size={size} aria-hidden="true" />
  if (state === 'failed') return <XCircle size={size} aria-hidden="true" />
  if (state === 'rejected' || state === 'cancelled') return <Ban size={size} aria-hidden="true" />
  return <>{index + 1}</>
}

/** DEV → UAT → PROD, one step per environment, reflecting the contract §1 stage status. */
export function StageStepper({ campaign, size = 'full', selected, onSelect }: StageStepperProps) {
  const mini = size === 'mini'
  return (
    <ol
      className={mini ? styles.miniStepper : styles.stepper}
      aria-label="Environment promotion progress"
      data-testid={mini ? 'stage-stepper-mini' : 'stage-stepper'}
    >
      {PATCH_ENVIRONMENTS.map((env, index) => {
        const stage = findStage(campaign, env)
        const included = isStageIncluded(stage)
        const state: DisplayState = included ? stepStateFor(stage.status) : 'absent'
        const status = included ? stage.status : 'absent'
        const statusLabel = included ? stageStatusLabel(stage.status) : 'Not in this campaign'
        const running = included && isRunningStatus(stage.status)
        const label = `${env.toUpperCase()}: ${statusLabel}`
        const connectorDone = state === 'done'

        const item = mini ? (
          <span
            className={clsx(styles.mini, MINI_CLASS[state])}
            title={label}
            aria-label={label}
            data-env={env}
            data-state={state}
            data-status={status}
          >
            <StateIcon state={state} running={running} index={index} size={10} />
            {env.toUpperCase()}
          </span>
        ) : (
          (() => {
            const content = (
              <>
                <span className={styles.stepIndex}>
                  <StateIcon state={state} running={running} index={index} size={12} />
                </span>
                <span className={styles.stepText}>
                  <span className={styles.stepEnv}>{env.toUpperCase()}</span>
                  <span className={styles.stepStatus}>{statusLabel}</span>
                </span>
              </>
            )
            const cls = clsx(styles.step, FULL_CLASS[state], selected === env && styles.stepSelected)
            const common = { 'data-env': env, 'data-state': state, 'data-status': status }
            return onSelect && included ? (
              <button
                type="button"
                className={cls}
                aria-pressed={selected === env}
                aria-label={label}
                onClick={() => onSelect(env)}
                {...common}
              >
                {content}
              </button>
            ) : (
              <div className={cls} aria-label={label} {...common}>
                {content}
              </div>
            )
          })()
        )

        return (
          <Fragment key={env}>
            <li className={styles.stepItem}>{item}</li>
            {index < PATCH_ENVIRONMENTS.length - 1 && (
              <li aria-hidden="true" className={styles.stepItem}>
                <span className={mini ? styles.miniConnector : clsx(styles.connector, connectorDone && styles.connectorDone)} />
              </li>
            )}
          </Fragment>
        )
      })}
    </ol>
  )
}
